import { useEffect, useState } from "react";
import { Tag, Trash2, Plus } from "lucide-react";
import { toast } from "sonner";
import { api, formatTL } from "@/lib/api";

const EMPTY = { code: "", type: "percent", value: "", expires_at: "", active: true };
const today = () => new Date().toLocaleDateString("sv-SE");

const CodeRow = ({ d, onToggle, onDelete }) => {
  const expired = d.expires_at && d.expires_at < today();
  return (
    <div data-testid={`discount-row-${d.code}`} className="flex flex-wrap items-center gap-3 px-4 py-3 border-b border-[var(--fb-border)] last:border-0">
      <span className="font-display font-extrabold text-lg tracking-wide">{d.code}</span>
      <span className="text-sm font-bold text-green-700">{d.type === "percent" ? `%${d.value}` : formatTL(d.value)} indirim</span>
      <span className="text-xs text-[var(--fb-muted)]">
        {d.expires_at ? `Son gün: ${new Date(d.expires_at + "T12:00:00").toLocaleDateString("tr-TR")}` : "Süresiz"} · {d.uses || 0} kullanım
      </span>
      {expired && <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">Süresi doldu</span>}
      <div className="ml-auto flex items-center gap-2">
        <button
          data-testid={`discount-toggle-${d.code}`}
          onClick={() => onToggle(d)}
          className={`text-xs font-bold px-3 py-1.5 rounded-full ${d.active ? "bg-green-600 text-white" : "bg-[var(--fb-neutral)] text-[var(--fb-muted)]"}`}
        >
          {d.active ? "Aktif" : "Pasif"}
        </button>
        <button data-testid={`discount-delete-${d.code}`} onClick={() => onDelete(d)} className="p-2 rounded-full hover:bg-red-50 text-[var(--fb-muted)] hover:text-[var(--fb-red)]">
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export const DiscountCodesPanel = () => {
  const [codes, setCodes] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const load = () => api.get("/discount-codes").then((r) => setCodes(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const err = (e) => toast.error(e?.response?.data?.detail || "İşlem başarısız");
  const create = async () => {
    try {
      await api.post("/discount-codes", { ...form, value: Number(form.value) });
      toast.success("İndirim kodu eklendi");
      setForm(EMPTY);
      load();
    } catch (e) { err(e); }
  };
  const toggle = async (d) => {
    try {
      await api.put(`/discount-codes/${d.id}`, { code: d.code, type: d.type, value: d.value, expires_at: d.expires_at || "", active: !d.active });
      load();
    } catch (e) { err(e); }
  };
  const remove = async (d) => {
    if (!window.confirm(`${d.code} kodu silinsin mi?`)) return;
    await api.delete(`/discount-codes/${d.id}`).catch(err);
    load();
  };

  const input = "border border-[var(--fb-border)] rounded-xl px-3 py-2.5 text-sm bg-white";
  return (
    <div data-testid="discount-codes-panel" className="space-y-6">
      <div className="bg-white border border-[var(--fb-border)] rounded-2xl p-5">
        <div className="flex items-center gap-2 mb-1">
          <Tag className="w-5 h-5 text-[var(--fb-red)]" />
          <h3 className="font-display font-bold text-lg">Yeni İndirim Kodu</h3>
        </div>
        <p className="text-sm text-[var(--fb-muted)] mb-4">Kodlar sadece bu şubenin kioskunda, ödeme ekranında geçerlidir.</p>
        <div className="grid sm:grid-cols-5 gap-3">
          <input data-testid="discount-form-code" placeholder="Kod (ör. YAZ10)" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase().replace(/\s/g, "") })} className={`${input} font-bold uppercase`} />
          <div className="flex rounded-xl border border-[var(--fb-border)] overflow-hidden">
            {[["percent", "% Yüzde"], ["amount", "₺ Tutar"]].map(([v, l]) => (
              <button key={v} data-testid={`discount-form-type-${v}`} onClick={() => setForm({ ...form, type: v })} className={`flex-1 text-sm font-bold py-2.5 ${form.type === v ? "bg-[var(--fb-ink)] text-white" : "bg-white text-[var(--fb-muted)]"}`}>
                {l}
              </button>
            ))}
          </div>
          <input data-testid="discount-form-value" type="number" min="0" placeholder={form.type === "percent" ? "Yüzde (ör. 10)" : "Tutar (ör. 50)"} value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} className={input} />
          <input data-testid="discount-form-expires" type="date" title="Son kullanma tarihi (boş = süresiz)" value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} className={input} />
          <button data-testid="discount-form-save" onClick={create} disabled={!form.code || !form.value} className="flex items-center justify-center gap-1.5 bg-[var(--fb-red)] disabled:opacity-40 text-white font-semibold rounded-full px-5 py-2.5">
            <Plus className="w-4 h-4" /> Kodu Ekle
          </button>
        </div>
        <p className="text-xs text-[var(--fb-muted)] mt-2">Son kullanma tarihini boş bırakırsanız kod süresiz geçerli olur.</p>
      </div>
      <div className="bg-white border border-[var(--fb-border)] rounded-2xl">
        {codes.length === 0 ? (
          <p data-testid="discount-empty" className="p-6 text-sm text-[var(--fb-muted)] text-center">Henüz indirim kodu yok.</p>
        ) : (
          codes.map((d) => <CodeRow key={d.id} d={d} onToggle={toggle} onDelete={remove} />)
        )}
      </div>
    </div>
  );
};
